import React from "react";
import styled from "@emotion/styled";
import { bbox as turfBBox } from "@turf/turf";
import type { FeatureCollection } from "geojson";

import GeoMap, { Source, Layer } from "@components/Map";
import Button from "@components/Button";
import Checkbox from "@components/Checkbox";
import Text from "@components/Text";
import { Row } from "@src/components/Flex";
import { useConvert, useLang, useYield } from "@src/hooks/useLang";
import {
  getHeatmapData,
  getProductivityMap,
  type TPrescriptionSources,
  type TPrescriptionLayerSpec,
  type TPrescriptionSurface,
} from "@services/fieldsService";
import type { PrescriptionLayersOption } from "@services/mowerService";

// One option in the picker: a yield season or the productivity map.
type LayerOption = {
  key: string;
  title: string;
  subtitle: string;
  spec: TPrescriptionLayerSpec;
  /** Does this layer cover the full boundary (productivity) or only harvested hexes (yield)? */
  fullSurface: boolean;
};

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 30;
  background: rgba(248, 250, 246, 0.96);
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  overflow-y: auto;
`;

const Title = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const Card = styled.div<{ selected: boolean }>`
  border: 2px solid ${({ selected }) => (selected ? "#68ad18" : "#e5e7eb")};
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease;
  box-shadow: ${({ selected }) =>
    selected ? "0 0 0 1px #68ad18" : "0 1px 3px rgba(0,0,0,0.08)"};
`;

const ThumbWrap = styled.div`
  position: relative;
  height: 150px;
  width: 100%;
  background: #eef1ec;
`;

const CardBody = styled.div`
  padding: 0.625rem 0.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SurfaceBox = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1rem;
`;

const SurfaceOption = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid ${({ selected }) => (selected ? "#68ad18" : "#e5e7eb")};
  background: ${({ selected }) => (selected ? "#f1f7e8" : "#fff")};
  flex: 1;
`;

/** Small read-only map preview of one layer's GeoJSON, colored by `fill`. */
const LayerThumbnail: React.FC<{ fieldId: string; spec: TPrescriptionLayerSpec }> = ({
  fieldId,
  spec,
}) => {
  const [data, setData] = React.useState<FeatureCollection | null>(null);
  const [empty, setEmpty] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (spec.type === "yield") {
          const res = await getHeatmapData(fieldId, spec.seasonId);
          if (!cancelled) {
            const fc = res.features as FeatureCollection;
            setData(fc);
            setEmpty(!fc.features?.length);
          }
        } else {
          const res = await getProductivityMap(fieldId);
          if (!cancelled) {
            const fc = (res.geojson as FeatureCollection) ?? null;
            setData(fc);
            setEmpty(!fc?.features?.length);
          }
        }
      } catch {
        if (!cancelled) setEmpty(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fieldId, spec]);

  if (empty || !data) {
    return (
      <ThumbWrap style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Text size={12} color="#9ca3af">
          {empty ? "No preview" : "Loading…"}
        </Text>
      </ThumbWrap>
    );
  }

  return (
    <ThumbWrap>
      <GeoMap
        mapStyle="mapbox://styles/mapbox/satellite-v9"
        attributionControl={false}
        interactive={false}
        onLoad={(e) => {
          try {
            const [minX, minY, maxX, maxY] = turfBBox(data) as [
              number,
              number,
              number,
              number
            ];
            e.target.fitBounds(
              [
                [minX, minY],
                [maxX, maxY],
              ],
              { padding: 12, animate: false, duration: 0 }
            );
          } catch {
            /* ignore */
          }
        }}
      >
        <Source id="thumb" type="geojson" data={data}>
          <Layer
            type="fill"
            id="thumb-fill"
            paint={{ "fill-color": ["get", "fill"], "fill-opacity": 0.85 }}
          />
        </Source>
      </GeoMap>
    </ThumbWrap>
  );
};

const LayerPicker: React.FC<{
  fieldId: string;
  sources: TPrescriptionSources;
  onConfirm: (combined: PrescriptionLayersOption) => void;
  onCancel: () => void;
}> = ({ fieldId, sources, onConfirm, onCancel }) => {
  const lang = useLang();
  const px = lang.prescription;
  const { convertYield } = useConvert();
  const yieldUnit = useYield()();

  // Not memoised: a handful of seasons, and the unit converter is a fresh
  // function every render (useConvert), so a memo would recompute anyway.
  const options: LayerOption[] = (() => {
    const opts: LayerOption[] = sources.yieldSeasons.map((s) => ({
      key: `yield:${s.seasonId}`,
      title: s.isCurrent ? `${s.seasonName} (${px.currentYield})` : s.seasonName,
      // avgYield arrives in c/ha (clean-weight applied); shown in the user's
      // unit. No cell count: grid resolution is not something users read.
      subtitle: `${px.yield} · ${convertYield(s.avgYield).toFixed(1)} ${yieldUnit}`,
      spec: { type: "yield", seasonId: s.seasonId },
      fullSurface: false,
    }));
    if (sources.productivity.available) {
      opts.push({
        key: "productivity",
        title: px.productivityMap,
        subtitle: `NDVI · ${sources.productivity.seasonsValid} ${px.seasons}`,
        spec: { type: "productivity" },
        fullSurface: true,
      });
    }
    return opts;
  })();

  // Default: current yield season ticked (fallback to first option).
  const defaultKey =
    (sources.currentSeasonId && `yield:${sources.currentSeasonId}`) || options[0]?.key;
  const [selected, setSelected] = React.useState<Set<string>>(
    new Set(defaultKey ? [defaultKey] : [])
  );
  const [surface, setSurface] = React.useState<TPrescriptionSurface>("union");

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Keep at least one ticked.
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectedOptions = options.filter((o) => selected.has(o.key));
  // With more than one layer the surfaces practically always differ — yield
  // maps have gaps where productivity (full boundary) and other seasons don't —
  // so offer the union/intersection choice whenever ≥2 layers are combined.
  const surfacesDiffer = selectedOptions.length >= 2;

  const confirm = () => {
    onConfirm({ layers: selectedOptions.map((o) => o.spec), surface });
  };

  return (
    <Backdrop>
      <Title>{px.selectLayersTitle}</Title>
      <Text size={14} color="#6B7280">
        {px.selectLayersHint}
      </Text>

      <CardsGrid>
        {options.map((o) => {
          const isSel = selected.has(o.key);
          return (
            <Card key={o.key} selected={isSel} onClick={() => toggle(o.key)}>
              <LayerThumbnail fieldId={fieldId} spec={o.spec} />
              <CardBody>
                <div>
                  <Text size={14} style={{ fontWeight: 600 }}>
                    {o.title}
                  </Text>
                  <Text size={12} color="#9ca3af">
                    {o.subtitle}
                  </Text>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={isSel} onChange={() => toggle(o.key)} />
                </div>
              </CardBody>
            </Card>
          );
        })}
      </CardsGrid>

      {surfacesDiffer && (
        <SurfaceBox>
          <Text size={14} style={{ fontWeight: 600, marginBottom: 8 }}>
            {px.surfaceTitle}
          </Text>
          <Text size={12} color="#6B7280" style={{ marginBottom: 10 }}>
            {px.surfaceHint}
          </Text>
          <Row style={{ gap: 12 }}>
            <SurfaceOption selected={surface === "union"}>
              <input
                type="radio"
                checked={surface === "union"}
                onChange={() => setSurface("union")}
              />
              <div>
                <Text size={13} style={{ fontWeight: 600 }}>
                  {px.surfaceUnion}
                </Text>
                <Text size={11} color="#9ca3af">
                  {px.surfaceUnionHint}
                </Text>
              </div>
            </SurfaceOption>
            <SurfaceOption selected={surface === "intersection"}>
              <input
                type="radio"
                checked={surface === "intersection"}
                onChange={() => setSurface("intersection")}
              />
              <div>
                <Text size={13} style={{ fontWeight: 600 }}>
                  {px.surfaceIntersection}
                </Text>
                <Text size={11} color="#9ca3af">
                  {px.surfaceIntersectionHint}
                </Text>
              </div>
            </SurfaceOption>
          </Row>
        </SurfaceBox>
      )}

      <Row justifyContent="flex-end" style={{ gap: 8, marginTop: "auto", paddingTop: 12 }}>
        <Button kind="outline" onClick={onCancel}>
          {px.cancel}
        </Button>
        <Button kind="primary" disabled={selected.size === 0} onClick={confirm}>
          {px.generate}
        </Button>
      </Row>
    </Backdrop>
  );
};

export default LayerPicker;
